import { type AxialCoord, hexDistance } from "../hex";
import { findHex, isPlainOwnHex } from "../run/board";
import type { RunRules } from "../types/rules";
import type { FactionId, HexState, RunState } from "../types/state";

/**
 * What a city is and what it reaches: complete structures, walls, city sites, working cities and the
 * land in their reach.
 */

/** A structure that is working: procgen kinds always, built kinds once their ready tick has passed. */
export function structureComplete(hex: HexState, tick: number): boolean {
  return (
    hex.structure !== "none" && (hex.structureReadyTick === 0 || hex.structureReadyTick <= tick)
  );
}

/** Walls a hex regrows to: rough ground's, a finished tower's, a city's (a neutral one's own), or the largest. */
export function wallMax(hex: HexState, rules: RunRules, tick: number): number {
  const rough = hex.terrain === "rough" ? rules.roughWalls : 0;
  const tower = hex.structure === "tower" && structureComplete(hex, tick) ? rules.towerWalls : 0;
  const city =
    hex.structure === "city"
      ? hex.owner === "none"
        ? rules.neutralCityWalls
        : structureComplete(hex, tick)
          ? rules.cityWalls
          : 0
      : 0;
  return Math.max(rough, tower, city);
}

/** True when a city of any owner, finished or not, sits closer than `cityMinSpacing`. */
export function cityTooClose(state: RunState, coord: AxialCoord): boolean {
  return state.hexes.some(
    (hex) =>
      hex.structure === "city" &&
      !hex.razed &&
      hexDistance(hex.coord, coord) < state.rules.cityMinSpacing,
  );
}

/** A plain own hex where a city may stand: no other city inside `cityMinSpacing`. Price aside. */
export function isCitySite(state: RunState, faction: FactionId, coord: AxialCoord): boolean {
  return isPlainOwnHex(findHex(state, coord), faction) && !cityTooClose(state, coord);
}

/** Working cities of one faction, the source of all its growth. */
export function workingCities(state: RunState, faction: FactionId): HexState[] {
  return state.hexes.filter((hex) => isWorkingCityOf(hex, faction, state.tick));
}

/**
 * True when the hex lies within `cityReach` of a working city of its own owner: the land a
 * faction actually works. Neutral and razed hexes are never in reach. The render layer uses
 * it for the "this land pays nothing" tell; `produce` uses the same test.
 */
export function inCityReach(state: RunState, coord: AxialCoord): boolean {
  const hex = findHex(state, coord);
  if (hex === undefined || hex.razed || hex.owner === "none") {
    return false;
  }
  return workingCities(state, hex.owner).some(
    (city) => hexDistance(city.coord, coord) <= state.rules.cityReach,
  );
}

/** A finished city of a faction: the anchor of its land (A51) and the source of its growth. */
export function isWorkingCity(hex: HexState, tick: number): boolean {
  return (
    hex.owner !== "none" && !hex.razed && hex.structure === "city" && structureComplete(hex, tick)
  );
}

/** A working city of this faction's own. */
export function isWorkingCityOf<Hex extends HexState>(
  hex: Hex | undefined,
  faction: FactionId,
  tick: number,
): hex is Hex {
  return hex !== undefined && hex.owner === faction && isWorkingCity(hex, tick);
}

/** The faction's own working city at `coord`; undefined when the hex is none. */
export function ownWorkingCity(
  state: RunState,
  faction: FactionId,
  coord: AxialCoord,
): HexState | undefined {
  const hex = findHex(state, coord);
  return isWorkingCityOf(hex, faction, state.tick) ? hex : undefined;
}
